import { array, boolean, mixed, object, string } from "yup";
import { useEffect, useState } from "react";

import { CUSTOM_FIELD_TYPE } from "@src/views/panel/custom-fields/misc/constants";
import { URL_REGEX } from "@src/utility/helpers/regex";
import Validations from "@src/utility/helpers/validations";
import { customFieldsSchema } from "../misc/utils";
import dayjs from "dayjs";
import size from "lodash/size";
import useAxios from "@hooks/use-axios";
import useFilters from "@src/views/common/hooks/use-filter";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    _id                : "",
    company_name       : "",
    csm                : "",
    sdr                : "",
    platform           : "",
    stage              : "",
    status             : "active",
    is_p_comp          : false,
    p_comp             : "",
    mrr                : 0,
    plan               : { label: "", value: "" },
    billing_name       : "",
    is_churn           : false,
    country            : "",
    cid                : "",
    domain             : "",
    onboarding_won_date: "",
    trial_date         : "",
    date               : "",
    tag                : [],
    churned_at         : "",
    r_churn            : "",
    demo_date          : "",
    invoice_raised_date: "",
    custom_attributes  : {}
};

const useUpsert = (props) => {
    const { platformId } = useParams();
    const {
        getCompanies,
        modalConfig: { row },
        handleModal
    } = props;
    const [customFields, setCustomFields] = useState([]);

    const { emptyObjValidation, numberValidation, regexValidation } =
    Validations();

    const { axiosInstance, loading } = useAxios({ base: "panel" });
    const { loadAsyncOptions } = useFilters();

    const fetchCustomFields = async () => {
        const response = await axiosInstance.get(`/field/${platformId}/client`);
        setCustomFields(response?.data || []);
    };

    useEffect(() => {
        if (platformId) {
            fetchCustomFields();
        }
    }, [platformId]);

    const methods = useForm({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(
            object({
                _id                : string(),
                company_name       : string().required("Name is required"),
                csm                : emptyObjValidation({ name: "CSM" }),
                sdr                : emptyObjValidation({ name: "SDR" }),
                stage              : emptyObjValidation({ name: "Stage" }),
                status             : string().required("Status is required"),
                mrr                : numberValidation({ name: "MRR" }),
                date               : string().required("Date is required"),
                platform           : mixed().nullable(),
                is_p_comp          : boolean().default(false),
                p_comp             : mixed().nullable(),
                billing_name       : string(),
                plan               : mixed().nullable(),
                country            : string(),
                is_churn           : boolean(),
                domain             : regexValidation({ regex: URL_REGEX }),
                cid                : string(),
                onboarding_won_date: string(),
                trial_date         : string(),
                tags               : array(),
                churned_at         : string().when("is_churn", {
                    is       : true,
                    then     : (schema) => schema.required("Churn date is required"),
                    otherwise: (schema) => schema.notRequired()
                }),
                r_churn            : string(),
                demo_date          : string(),
                invoice_raised_date: string(),
                custom_attributes  : customFieldsSchema(customFields)
            })
        )
    });

    const {
        reset,
        handleSubmit,
        formState: { isDirty }
    } = methods;

    const populateAndResetForm = async (row) => {
        const getAsyncValue = (type, id) => new Promise((resolve) => {
            loadAsyncOptions({
                value   : "",
                callback: (options) => {
                    const matched = options.find((opt) => opt.value === id);
                    resolve(matched || null);
                },
                type,
                statusCheck: false
            });
        });

        const [csmOption, sdrOption, pCompOption] = await Promise.all([
            getAsyncValue("csm", row.csm?.id),
            getAsyncValue("sdr", row.sdr?.id),
            getAsyncValue("parents", row.p_comp?._id)
        ]);

        const formattedCustomAttributes = {};
        customFields.forEach((field) => {
            const value = row.custom_attributes?.[field.key];

            if (field.type === CUSTOM_FIELD_TYPE.SELECT) {
                const option = field.options?.find((opt) => opt.value === value);
                formattedCustomAttributes[field.key] = option ? option.value : null;
            } else if (field.type === CUSTOM_FIELD_TYPE.CHECKBOX) {
                formattedCustomAttributes[field.key] = Boolean(value);
            } else {
                formattedCustomAttributes[field.key] = value ?? "";
            }
        });

        const formData = {
            ...row,
            csm                : csmOption,
            sdr                : sdrOption,
            plan               : row.plan?.name ? { label: row.plan.name, value: row.plan._id } : "",
            stage              : row.stage?.name ? { label: row.stage.name, value: row.stage._id } : "",
            is_churn           : !!row.is_churn,
            date               : row.date ? dayjs(row.date).toDate() : "",
            onboarding_won_date: row.onboarding_won_date ? dayjs(row.onboarding_won_date).toDate() : "",
            trial_date         : row.trial_date ? dayjs(row.trial_date).toDate() : "",
            demo_date          : row.demo_date ? dayjs(row.demo_date).toDate() : "",
            invoice_raised_date: row.invoice_raised_date ? dayjs(row.invoice_raised_date).toDate() : "",
            churned_at         : row.churned_at ? dayjs(row.churned_at).toDate() : "",
            mrr                : row.mrr,
            platform           : {
                label: row.platform?.name,
                value: row.platform?._id
            },
            is_p_comp: row.is_p_comp,
            p_comp   : pCompOption,
            tags     : row.tags
                ?.filter((item) => item.name)
                .map((item) => ({ value: item._id, label: item.name })),
            custom_attributes: formattedCustomAttributes
        };
        reset(formData);
    };

    useEffect(() => {
        if (size(row) && customFields.length > 0) {
            populateAndResetForm(row);
        } else if (!size(row)) {
            reset(defaultValues);
        }
    }, [row, customFields]);

    const onSubmit = async () => {
        handleSubmit(async (formData) => {
            const isEdit = !!row._id;
            const method = !isEdit ? "post" : "patch";
            const url = !isEdit ? `/client/${platformId}` : `/client/${platformId}/${row._id}`;

            const payload = {
                company_name       : formData.company_name || "",
                country            : formData.country || "",
                csm                : formData.csm?.value || "",
                sdr                : formData.sdr?.value || "",
                date               : formData.date ? dayjs(formData.date).format("YYYY-MM-DD") : "",
                domain             : formData.domain || "",
                is_churn           : formData.is_churn,
                churned_at         : formData.churned_at ? dayjs(formData.churned_at).format("YYYY-MM-DD") : "",
                r_churn            : formData.r_churn || "",
                mrr                : Number(formData.mrr) || 0,
                plid               : formData?.plan?.value || "",
                stage              : formData.stage.value || "",
                status             : formData.status || "",
                cid                : formData.cid || "",
                onboarding_won_date: formData.onboarding_won_date ? dayjs(formData.onboarding_won_date).format("YYYY-MM-DD") : "",
                trial_date         : formData.trial_date ? dayjs(formData.trial_date).format("YYYY-MM-DD") : "",
                demo_date          : formData.demo_date ? dayjs(formData.demo_date).format("YYYY-MM-DD") : "",
                invoice_raised_date: formData.invoice_raised_date ? dayjs(formData.invoice_raised_date).format("YYYY-MM-DD") : "",
                is_parent_company  : formData.is_p_comp || false,
                parent_company     : formData.p_comp?.value || "",
                tags               : formData.tags?.map((item) => item.value),
                custom_attributes  : formData.custom_attributes || {}
            };
            await axiosInstance[method](url, payload);
            getCompanies();
            handleModal();
        })();
    };

    return {
        methods,
        isDirty,
        onSubmit,
        loading,
        customFields
    };
};

export default useUpsert;
